/**
 * ReserveSlotDrawer.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Reserva um horário para um paciente que ainda vai preencher o cadastro
 * (docs/features/appointment-reservation/README.md).
 *
 * Com telefone, profissional e um ou mais serviços o backend cria o bloqueio
 * "Reservado: ...", gera o link de cadastro e, com número próprio, já envia a
 * mensagem. Quando o paciente se cadastra, a reserva vira agendamento sozinha. Se
 * o telefone já tem cadastro, oferece "Agendar agora" para a mesma pessoa, mas o
 * link continua disponível: outra pessoa pode usar o mesmo telefone (um filho, um
 * dependente).
 *
 * Serviços: mesmo ProcedureMultiSelect do AppointmentDrawer (linhas
 * { procedure_id, minutes, price }); a duração do bloqueio é a soma e o POST leva
 * `procedures` no formato da API (docs/features/agendamento-multiplos-procedimentos).
 *
 * Plano: o mesmo do link de cadastro (Profissional ou superior); bloqueado, o
 * drawer continua visível com o banner e o salvar abre o modal de upgrade.
 *
 * @module ReserveSlotDrawer
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import { WEEKDAYS_LONG, MONTHS } from '../../constants/calendarConstants';
import usePlanLock from '../../../../../hooks/usePlanLock';
import { SUBSCRIPTION_PERMISSIONS } from '../../../../../consts/SubscriptionFeatures';
import { maskPhone, onlyDigits } from '../../../../PublicPatientRegistration/registrationMasks';
import { formatReservationPhone } from '../../utils/reservationMessage';
import { minutesToDuration } from '../../utils/appointmentUtils';
import ProcedureMultiSelect from '../ProcedureMultiSelect/ProcedureMultiSelect';
import { lineFromProcedure, linesToApi, linesTotals } from '../ProcedureMultiSelect/procedureLines';
import ReservationShare from './ReservationShare';
import baseStyles from '../BlockSlotDrawer/BlockSlotDrawer.module.css';
import styles from './ReserveSlotDrawer.module.css';

const XIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

export const HourglassIcon = ({ color = '#5A80FB' }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h12M6 22h12"/>
    <path d="M7 2v4a5 5 0 0 0 10 0V2M7 22v-4a5 5 0 0 1 10 0v4"/>
  </svg>
);

const DEFAULT_DURATION_MINUTES = 60;

const pad = (n) => String(n).padStart(2, '0');

/** Últimos 10 dígitos: DDD + número, ignorando DDI e máscara. */
const phoneKey = (value) => onlyDigits(value).slice(-10);

/**
 * @param {Object}   props
 * @param {boolean}  props.isOpen
 * @param {Object}   props.slot - { date: Date, hour: number, minute: number }
 * @param {Function} props.onClose
 * @param {Function} [props.onCreated] - reserva criada (o calendário recarrega; o drawer segue aberto com o envio)
 * @param {Function} [props.onScheduleExisting] - (patient, { resourceId, procedureId, procedures, roomId })
 *   telefone já é de um paciente; `procedures` são as linhas da tela ({ procedure_id, minutes, price })
 * @param {Function} [props.onGhostChange]
 */
export default function ReserveSlotDrawer({
  isOpen,
  slot,
  onClose,
  onCreated,
  onScheduleExisting,
  onGhostChange,
}) {
  const { CompanyConfig, CreateAppointmentReservation, patients } = DataProviderInstance();
  const planLock = usePlanLock(SUBSCRIPTION_PERMISSIONS.PATIENT_REGISTRATION_LINK, 'A reserva de horário com link de cadastro');

  const resources = useMemo(
    () => (CompanyConfig?.Resources || []).filter(r => r.Active == null || r.Active == 1), // eslint-disable-line eqeqeq
    [CompanyConfig]
  );
  const procedures = useMemo(
    () => (CompanyConfig?.Procedures || []).filter(p => p.Active == null || p.Active == 1), // eslint-disable-line eqeqeq
    [CompanyConfig]
  );
  const rooms = Array.isArray(CompanyConfig?.Rooms) ? CompanyConfig.Rooms : [];

  const [countryCode, setCountryCode] = useState('55');
  const [phone, setPhone] = useState('');
  const [resourceId, setResourceId] = useState('');
  const [procedureLines, setProcedureLines] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  /** Linha inicial a partir do cadastro: o serviço padrão do profissional ou o único cadastrado. */
  const linesForProcedure = useCallback((id) => {
    const proc = procedures.find(p => String(p.Id) === String(id));
    return proc ? [lineFromProcedure(proc)] : [];
  }, [procedures]);

  useEffect(() => {
    if (!isOpen) return;
    setCountryCode(String(CompanyConfig?.countryCode || '55'));
    setPhone('');
    setError('');
    setIsLoading(false);
    setResult(null);
    const only = resources.length === 1 ? resources[0] : null;
    setResourceId(only ? String(only.Id) : '');
    const defaultProc = only?.DefaultProcedure || (procedures.length === 1 ? procedures[0].Id : '');
    setProcedureLines(defaultProc ? linesForProcedure(defaultProc) : []);
    setRoomId('');
  }, [isOpen, slot]); // eslint-disable-line react-hooks/exhaustive-deps

  // Duração do bloqueio = soma das linhas (sem linha, o padrão só para o fantasma)
  const totals = linesTotals(procedureLines);
  const durationMinutes = procedureLines.length > 0 ? totals.minutes : DEFAULT_DURATION_MINUTES;
  const phoneDigits = onlyDigits(phone);

  const existingPatient = useMemo(() => {
    if (phoneDigits.length < 10) return null;
    const key = phoneKey(phoneDigits);
    return (patients || []).find(p => p?.phone && phoneKey(p.phone) === key) || null;
  }, [patients, phoneDigits]);

  // ── Fantasma no calendário enquanto o formulário está aberto ──────────────
  useEffect(() => {
    if (!isOpen || !slot || !onGhostChange || result) return;
    const d = slot.date;
    onGhostChange({
      id: '__ghost__',
      status: 'blocked',
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(slot.hour)}:${pad(slot.minute)}:00`,
      duration: minutesToDuration(durationMinutes),
      patient: phoneDigits.length >= 10 ? `Reservado: ${formatReservationPhone(countryCode, phoneDigits)}` : 'Reservado',
      resource: resourceId || null,
    });
    return () => onGhostChange(null);
  }, [isOpen, slot, durationMinutes, phoneDigits, countryCode, resourceId, result, onGhostChange]);

  const handleResourceChange = (e) => {
    const id = e.target.value;
    setResourceId(id);
    const resource = resources.find(r => String(r.Id) === String(id));
    if (resource?.DefaultProcedure) setProcedureLines(linesForProcedure(resource.DefaultProcedure));
  };

  const handleSave = useCallback(() => {
    if (phoneDigits.length < 10) { setError('Informe o telefone do paciente com DDD.'); return; }
    if (!resourceId) { setError('Escolha a agenda.'); return; }
    if (procedureLines.length === 0) { setError('Escolha ao menos um serviço.'); return; }

    setError('');
    setIsLoading(true);
    const d = slot.date;
    const apiLines = linesToApi(procedureLines);
    CreateAppointmentReservation({
      date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(slot.hour)}:${pad(slot.minute)}:00`,
      resourceId: Number(resourceId),
      procedureId: apiLines[0].procedure_id,  // primeira linha (compatibilidade)
      roomId: roomId ? Number(roomId) : null,
      duration: minutesToDuration(durationMinutes), // soma das linhas
      procedures: apiLines,                    // [{ procedure_id, duration, price }]
      countryCode: onlyDigits(countryCode),
      phone: phoneDigits,
    }, (res) => {
      setIsLoading(false);
      if (!res?.success) {
        setError(res?.error || res?.message || 'Não foi possível reservar o horário. Tente novamente.');
        return;
      }
      setResult(res);
      onCreated?.();
    });
  }, [phoneDigits, resourceId, procedureLines, roomId, slot, durationMinutes, countryCode, CreateAppointmentReservation, onCreated]);

  if (!isOpen || !slot) return null;

  const dateLabel = `${slot.date.getDate()} ${MONTHS[slot.date.getMonth()]} ${slot.date.getFullYear()} • ${WEEKDAYS_LONG[slot.date.getDay()]}`;
  const endTotal = slot.hour * 60 + slot.minute + durationMinutes;
  const timeLabel = `${pad(slot.hour)}:${pad(slot.minute)} às ${pad(Math.floor(endTotal / 60) % 24)}:${pad(endTotal % 60)}`;

  return (
    <div className={baseStyles.drawerContainer} role="dialog" aria-modal="true" aria-label="Reservar para paciente">
      <div className={baseStyles.drawerHeader}>
        <div className={baseStyles.drawerHeaderTop}>
          <h2 className={baseStyles.drawerTitle}>{result ? 'Horário reservado' : 'Reservar para paciente'}</h2>
          <button className={baseStyles.drawerCloseBtn} onClick={onClose} aria-label="Fechar" title="Fechar" type="button">
            <XIcon />
          </button>
        </div>
        <div className={baseStyles.drawerHeaderDivider} />
        <div className={baseStyles.slotContext}>
          <div className={baseStyles.slotContextLeft}>
            <span className={baseStyles.slotContextIcon} aria-hidden="true"><HourglassIcon /></span>
            <div className={baseStyles.slotContextInfo}>
              <span className={baseStyles.slotDate}>{dateLabel}</span>
              <div className={baseStyles.slotTimeRow}>
                <span className={baseStyles.slotTime}>{timeLabel}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={baseStyles.drawerBody}>
        {result ? (
          <ReservationShare data={result} />
        ) : (
          <>
            {planLock.banner}

            <p className={styles.intro}>
              O horário fica segurado e o paciente recebe o link para preencher o cadastro.
              Quando ele terminar, a reserva vira agendamento sozinha.
            </p>

            {error && <div className={baseStyles.validationError} role="alert">{error}</div>}

            <div className={baseStyles.formSection}>
              <label className={baseStyles.fieldLabel} htmlFor="reserve-phone">Telefone do paciente</label>
              <div className={styles.phoneRow}>
                <input
                  className={baseStyles.fieldInput}
                  value={countryCode}
                  onChange={e => setCountryCode(onlyDigits(e.target.value).slice(0, 3))}
                  aria-label="DDI"
                  inputMode="numeric"
                />
                <input
                  id="reserve-phone"
                  className={baseStyles.fieldInput}
                  value={phone}
                  onChange={e => setPhone(maskPhone(e.target.value))}
                  placeholder="(11) 98888-7777"
                  inputMode="tel"
                  autoFocus
                />
              </div>
            </div>

            {existingPatient && (
              <div className={styles.existingPatient}>
                <span>
                  <strong>{existingPatient.name}</strong> já tem cadastro com este telefone.
                  Se for a mesma pessoa, agende direto; se for outra pessoa, reserve e envie o link.
                </span>
                <div>
                  <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={() => onScheduleExisting?.(existingPatient, {
                      resourceId,
                      procedureId: procedureLines[0]?.procedure_id || '',
                      procedures: procedureLines,
                      roomId,
                    })}
                  >
                    Agendar agora
                  </button>
                </div>
              </div>
            )}

            <div className={baseStyles.formSection}>
              <label className={baseStyles.fieldLabel} htmlFor="reserve-resource">Agenda</label>
              <select id="reserve-resource" className={baseStyles.fieldSelect} value={resourceId} onChange={handleResourceChange}>
                <option value="">Selecionar agenda...</option>
                {resources.map(r => <option key={r.Id} value={String(r.Id)}>{r.Label}</option>)}
              </select>
            </div>

            <div className={baseStyles.formSection}>
              <label className={baseStyles.fieldLabel}>Serviços</label>
              <ProcedureMultiSelect
                procedures={procedures}
                value={procedureLines}
                onChange={(lines) => {
                  setProcedureLines(lines);
                  if (lines.length > 0) setError('');
                }}
              />
              <span className={styles.hint}>
                Um ou mais serviços; a duração e o valor de cada um vêm do cadastro, como num agendamento.
              </span>
            </div>

            {rooms.length > 0 && (
              <div className={baseStyles.formSection}>
                <label className={baseStyles.fieldLabel} htmlFor="reserve-room">Sala</label>
                <select id="reserve-room" className={baseStyles.fieldSelect} value={roomId} onChange={e => setRoomId(e.target.value)}>
                  <option value="">Sem sala</option>
                  {rooms.map(r => (
                    <option key={r.Id || r.id} value={String(r.Id || r.id)}>{r.Label || r.label || r.Name || r.name}</option>
                  ))}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      <div className={baseStyles.drawerFooterDivider} />
      <div className={baseStyles.drawerFooter}>
        {result ? (
          <button type="button" className={baseStyles.btnSave} onClick={onClose}>Concluir</button>
        ) : (
          <>
            <button type="button" className={baseStyles.btnCancel} onClick={onClose} disabled={isLoading}>Cancelar</button>
            <button
              type="button"
              className={baseStyles.btnSave}
              onClick={planLock.guard(handleSave)}
              disabled={isLoading}
            >
              {isLoading && <span className={baseStyles.spinner} />}
              {isLoading ? 'Reservando...' : 'Reservar e enviar link'}
            </button>
          </>
        )}
      </div>

      {planLock.modal}
    </div>
  );
}
